import { Fragment, useEffect, useRef, useState } from "react"
import FormField from "./FormField"
import Sidebar from "./Sidebar"
import { useRouter } from "next/router"
import { useMemo } from "react"
import classNames from "classnames"
import { useTranslations } from "lib/useTranslations.client"
import usePageData from "lib/usePageData"
import { countries, needsSubdivision, subdivisionsFor } from "lib/geo/countries"
import { countryCodeFromLocale, preselectCountryCode } from "lib/geo/visitorCountry"
import { postLead } from "lib/leadFormClient"
import LeadFormError from "./LeadFormError"

export default function Downloads({
  fileGroups,
  globalSettings = {},
}) {
  const { downloadSettings } = globalSettings
  const { t } = useTranslations()
  const pageData = usePageData()
  const prefillRef = useRef(preselectCountryCode(pageData?.visitorCountryCode))
  const countryTouched = useRef(false)

  const [isOpen, setIsOpen] = useState(false)
  const [inputs, setInputs] = useState({
    email: "",
    country: prefillRef.current,
    region: "",
  })
  const [status, setStatus] = useState(false)
  const [selectedFiles, setSelectedFiles] = useState([])

  const fileGroupsMemo = useMemo(() => {
    return fileGroups.map((fileGroup) => {
      return {
        ...fileGroup,
        files: fileGroup.files.map(({ title, file, _key }) => {
          return {
            _key,
            title,
            url: file.asset.url,
            isSelected: selectedFiles.includes(_key),
          }
        }),
      }
    })
  }, [fileGroups, selectedFiles])

  const router = useRouter()
  const onInputChange = (e) => {
    setInputs((input) => ({
      ...input,
      [e.target.name]: e.target.value,
    }))
  }

  const onCountryChange = (e) => {
    countryTouched.current = true
    const country = e.target.value
    setInputs((input) => ({
      ...input,
      country,
      region: "",
    }))
  }

  useEffect(() => {
    let cancelled = false

    const apply = (code, overwrite) => {
      const next = preselectCountryCode(code)
      if (!next || cancelled || countryTouched.current) return
      setInputs((current) => {
        if (countryTouched.current) return current
        if (!overwrite && current.country) return current
        if (current.country === next) return current
        prefillRef.current = next
        return { ...current, country: next, region: "" }
      })
    }

    fetch("/api/visitor-country", { cache: "no-store" })
      .then((response) => (response.ok ? response.json() : null))
      .then((body) => {
        if (preselectCountryCode(body?.country)) apply(body.country, true)
        else apply(countryCodeFromLocale(window.navigator?.language), false)
      })
      .catch(() => apply(countryCodeFromLocale(window.navigator?.language), false))

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    const onHashChangeStart = () => {
      setIsOpen(window.location.hash.indexOf("#/downloads") > -1)
    }

    window.addEventListener("hashchange", onHashChangeStart)
    onHashChangeStart()

    return () => window.removeEventListener("hashchange", onHashChangeStart)
  }, [])

  const onSubmit = async (e) => {
    e.preventDefault()
    if (status === "LOADING") return
    setStatus("LOADING")

    const files = fileGroupsMemo
      .map((fileGroup) => fileGroup.files)
      .flat(Infinity)
      .filter((file) => file.isSelected)
      .map((file) => {
        return {
          title: file.title,
          url: file.url,
        }
      })

    const payload = {
      email: inputs.email,
      country: inputs.country,
      files,
    }
    if (needsSubdivision(inputs.country)) payload.region = inputs.region

    const result = await postLead("/api/download-files", payload, {
      headers: {
        'Accept-Language': router.locale
      }
    })

    if (!result.ok) {
      setStatus("ERROR")
      return
    }

    e.target.reset()
    setInputs({ email: "", country: prefillRef.current, region: "" })
    setStatus("SUCCESS")
  }

  const closeModal = () => {
    router.push("/", undefined, { shallow: true })
    setIsOpen(false)
  }

  const onClickCheck = (fileKey) => {
    if (selectedFiles.includes(fileKey)) {
      setSelectedFiles(selectedFiles.filter((file) => file !== fileKey))
      return
    }

    setSelectedFiles([...selectedFiles, fileKey])
  }

  return (
    <Sidebar isOpen={isOpen} onClose={closeModal}>
      <div className="p-4 lg:p-8">
        <h2 className="text-2xl mb-8">Download tools and guides</h2>
        <form onSubmit={onSubmit} data-testid="downloads-form">
          {fileGroupsMemo.map(({ _id, title, files }) => (
            <div key={_id} className="border-t border-secondary pt-6 my-6">
              <h3 className="text-xl mb-2">{title}</h3>
              {files.map(({ _key, title, url, isSelected }) => (
                <Fragment key={_key}>
                  {status === "SUCCESS" && isSelected ? (
                    // <a
                    //   href={url}
                    //   target="_blank"
                    //   className="flex items-center py-2 underline"
                    // >
                    //   <img
                    //     src="/assets/icons/download.svg"
                    //     className="mr-2 w-[13px]"
                    //   />
                    title
                  ) : (
                    // </a>
                    <label className="py-2 flex items-start">
                      <input
                        type="checkbox"
                        className="mr-2 mt-1"
                        checked={isSelected}
                        disabled={status === "SUCCESS" && !isSelected}
                        onChange={() => onClickCheck(_key)}
                      />
                      {title}
                    </label>
                  )}
                </Fragment>
              ))}
            </div>
          ))}
          <DownloadSelect
            name="country"
            value={inputs.country}
            onChange={onCountryChange}
            label={t("forms.country", "Country")}
            testId="downloads-country"
            requiredLabel={t("forms.required")}
            options={countries()}
          />
          {needsSubdivision(inputs.country) && (
            <DownloadSelect
              name="region"
              value={inputs.region}
              onChange={onInputChange}
              label={t("forms.state", "State / Province")}
              testId="downloads-region"
              requiredLabel={t("forms.required")}
              options={subdivisionsFor(inputs.country)}
            />
          )}
          <FormField
            name="email"
            placeholder={t("forms.email")}
            type="email"
            onChange={onInputChange}
            value={inputs.email}
            required
          />
          {status === "SUCCESS" && (
            <p className="whitespace-pre-line" data-testid="downloads-success">
              {downloadSettings.formConfirmationText}
            </p>
          )}
          {status === "ERROR" && (
            <LeadFormError
              testId="downloads-error"
              settings={globalSettings}
              message={t('forms.error', 'Something went wrong and your downloads were not sent. Please try again.')}
            />
          )}

          <button
            type="submit"
            className={classNames("btn btn-primary", {
              "opacity-80 pointer-events-none": status === "LOADING",
            })}
            disabled={status === "LOADING"}
            data-testid="downloads-submit"
          >
            {status === "LOADING"
              ? t("forms.sending", "Sending…")
              : downloadSettings.downloadButtonLabel}
          </button>
        </form>
      </div>
    </Sidebar>
  )
}

function DownloadSelect({
  name,
  value,
  onChange,
  label,
  testId,
  requiredLabel,
  options,
}) {
  return (
    <div className="mb-4 relative">
      {requiredLabel && !value && (
        <div className="border border-transparent absolute right-4 pointer-events-none text-xs flex items-center inset-y-1">
          {requiredLabel}
        </div>
      )}
      <select
        name={name}
        required
        value={value}
        onChange={onChange}
        aria-label={label}
        data-testid={testId}
        className={classNames(
          "border border-grayed-muted px-4 py-2 rounded-full w-full text-lg bg-white appearance-none",
          value ? "text-inherit" : "text-gray-400"
        )}
      >
        <option value="">{label}</option>
        {options.map((option) => (
          <option key={option.code} value={option.code}>
            {option.name}
          </option>
        ))}
      </select>
    </div>
  )
}
